import { useCardData } from 'components/providers/CardDataProvider'
import { handleFetchOrders } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import {
  ArticleParams,
  Card,
  CardDataItem,
  CardmarketCondition,
  CountryEdition,
  countryEditionMap,
  GAME_NAMES,
  GRADING_COMPANY_LABELS,
  mapBackendFinishAttributeToInternal,
  Order,
  OrderArticle,
  OrderArticleResponseDto,
  OrderResponseDto,
} from 'shared'
import { shallow } from 'zustand/shallow'
import { useOrderStore } from './useOrderStore'

export const useFetchOrders = () => {
  const [
    orders,

    setOrders,
    setRawOrders,
    setLoadingFromMkm,
    setLoadingFromPt,
    setInitializedOrders,
  ] = useOrderStore(
    (s) => [
      s.orders,
      s.setOrders,
      s.setRawOrders,
      s.setLoadingFromMkm,
      s.setLoadingFromPt,
      s.setInitializedOrders,
    ],
    shallow
  )
  orders
  const [loading, setLoading] = useState(true)
  const cardData = useCardData()
  const { enqueueSnackbar } = useSnackbar()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  const hydrateArticle = (
    article: OrderArticleResponseDto,
    dto: OrderResponseDto,
    pos: string
  ): OrderArticle => {
    const attributes: ArticleParams['attributes'] = [
      Number(article.quantity),
      article.condition as CardmarketCondition,
      Number(article.language.idLanguage),
      Number(article.mkmProductId),
      Number(article.price),
      article.comment,
      false, // isPlayset
      article.isSigned,
      article.isFirstEd,
      '', // idArticle
      null, // listedAt
      false, // isAltered
      GRADING_COMPANY_LABELS.find((entry) => entry.code === article.gradingCompany)?.code ??
        undefined,
      article.grade ?? undefined,
      countryEditionMap.find((entry) => entry.code === article.countryEdition)?.value ??
        CountryEdition.Regular,
      mapBackendFinishAttributeToInternal(article.finishType),
      Number(article.ptArticleId),
    ]

    let card = cardDataMap.get(article.mkmProductId)
    if (!card) {
      const locNames: CardDataItem['locNames'] = {
        DE: undefined,
        IT: undefined,
        FR: undefined,
        ES: undefined,
      }

      if (article.product.locName && article.product.locName !== article.product.enName) {
        switch (article.language?.idLanguage) {
          case 2:
            locNames.FR = article.product.locName
            break
          case 3:
            locNames.DE = article.product.locName
            break
          case 4:
            locNames.ES = article.product.locName
            break
          case 5:
            locNames.IT = article.product.locName
            break
        }
      }

      const cardDataItem: CardDataItem = {
        _id: article.mkmProductId,
        rarity: 0,
        cn: article.product.nr ?? null,
        name: article.product.enName,
        set: article.product.expansionCode ?? '',
        expansion: article.product.expansion ?? '',
        onlyFoil: undefined,
        locNames,
        rarityStr: article.product.rarity ?? undefined,
        categoryName: article.product.category ?? undefined,
        expansionReleaseDate: article.product.expansionReleaseDate ?? undefined,
        colors: undefined,
        idGame: article.product.idGame ?? 99,
      }

      card = new Card({ cardDataItem })
      cardDataMap.set(article.mkmProductId ?? 0, card)
    }

    return new OrderArticle({
      attributes,
      card: card.toPlainObject(),
      articleState: article.articleState,
      issues: article.issues,
      ptOrderId: Number(dto.ptOrderId),
      state: dto.state,
      pos,
      currencyCode: article.currency,
      pickingId: dto.picking?.pickingId,
      pickedQuantity: article.pickedQuantity,
      ptOrderArticleId: Number(article.ptOrderArticleId),
      source: dto.source,
      sourceArticleId: article.sourceArticleId,
      sourceOrderId: String(dto.sourceOrderId),
      updatedAt: new Date(article.updatedAt),
      // TODO: locationInfo is typed as a single Location but we assign the whole array here.
      // No consumer in the Orders flow reads it today; if these articles ever flow into the
      // picking flow, locationInfo readers (PickingPresenter, ExportPickingList, ...) will break.
      locationInfo: article.locations as any,
      locations: article.locations.map((loc) => ({ ...loc })),
      buyer: { ...(dto.buyer as any) },
    })
  }

  const convertOrderResponseDtoToOrderClass = (dto: OrderResponseDto) => {
    const orderArticles: OrderArticle[] = []

    dto.articles?.forEach((article, index) => {
      const pos = `${index + 1}/${dto.articles?.length}`
      orderArticles.push(hydrateArticle(article, dto, pos))
    })

    return new Order({
      ptOrderId: dto.ptOrderId as number,
      source: dto.source,
      sourceOrderId: String(dto.sourceOrderId),
      articlesCount: dto.articlesCount,
      totalValue: dto.totalValue,
      articleValue: dto.articleValue,
      isCtZero: dto.isCtZero,
      isCtZeroClosing: dto.isCtZeroClosing,
      isPresale: dto.isPresale,
      currency: dto.currency,
      buyer: dto.buyer,
      shipping: dto.shipping,
      state: dto.state,
      picking: dto.picking,
      articleGames: (dto.articleGames ?? []).map((id) => GAME_NAMES[id] ?? 'Unknown'),
      articleCategories: dto.articleCategories ?? ['Unknown'],
      articles: orderArticles,
      updatedAt: dto.updatedAt as any,
      issues: dto.issues,
      note: dto.note,
    })
  }

  const initializeOrderStore = useCallback((data: OrderResponseDto[]) => {
    const orders: Order[] = data.map((dto) => convertOrderResponseDtoToOrderClass(dto))

    setOrders(orders)
    setInitializedOrders(true)
    setLoadingFromMkm(false)
    setLoadingFromPt(false)
  }, [])

  const fetchOrders = useCallback(async () => {
    setLoading(true)
    setLoadingFromPt(true)

    try {
      const data = await handleFetchOrders()

      if (!data?.length) {
        setRawOrders([])
        setLoadingFromPt(false)
        return
      }

      setRawOrders(data)
      initializeOrderStore(data)
    } catch (e) {
      setLoadingFromPt(false)
      enqueueSnackbar('Error loading orders', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [setLoadingFromPt, setRawOrders, initializeOrderStore, enqueueSnackbar])

  return { fetchOrders, loading, convertOrderResponseDtoToOrderClass }
}
